import { useEffect, useState } from 'react'
import { db } from './config'
import { client } from '../hooks/Client'
import describeQueryError from '../helpers/firestore/describeQueryError'
import notDeleted from '../helpers/notDeleted'

// Shared collection subscription cache.
//
// Multiple cards on the dashboard need the same tenant-scoped collections
// (e.g. Stakeholders, Research). Calling useFirestore('Research') in N cards
// opens N backend listeners for identical data. This hook opens ONE
// onSnapshot per (collection × compagny) and multicasts updates to all
// consumers, with ref-counted teardown when the last consumer unmounts.
//
// Soft deletes (ADR-008) are filtered here, in the snapshot handler. This hook
// opens its own onSnapshot and therefore bypasses the useFirestoreQuery engine
// that filters every other read — without this, soft-deleted docs keep counting
// in every consumer. Filtering client-side rather than with
// `.where('Deleted','==',false)` is deliberate: that query drops docs that lack
// the field entirely, which is most of them.
// AI-CONTEXT: See docs/pitfalls.md#database-missing-compagnyid-on-fresh-fetch

const cache = new Map()

const ensureEntry = (collection) => {
    const key = `${collection}:${client}`
    const existing = cache.get(key)
    if (existing) return existing

    const entry = {
        docs: undefined,
        listeners: new Set(),
        unsub: null,
    }
    entry.unsub = db
        .collection(collection)
        .where('CompagnyID', '==', client)
        .onSnapshot((snap) => {
            const next = []
            snap.forEach((d) => {
                const data = { ...d.data(), docid: d.id }
                if (notDeleted(data)) next.push(data)
            })
            entry.docs = next
            entry.listeners.forEach((cb) => cb(next))
        }, (error) => console.error(
            describeQueryError(collection, [['CompagnyID', '==', client]], error), error
        ))
    cache.set(key, entry)
    return entry
}

const useSharedCollection = (collection) => {
    const [docs, setDocs] = useState(() => {
        if (!collection) return undefined
        const entry = cache.get(`${collection}:${client}`)
        return entry?.docs
    })

    useEffect(() => {
        // Geen naam = niets abonneren. Zo kan een afnemer een collectie
        // overslaan die hij op dit scherm niet nodig heeft, zonder de hook
        // voorwaardelijk aan te roepen. `undefined` blijft staan, en dat is
        // precies wat afnemers al lezen als 'nog niet geladen'.
        // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
        if (!collection) return undefined

        const entry = ensureEntry(collection)
        const cb = (next) => setDocs(next)
        entry.listeners.add(cb)
        if (entry.docs !== undefined) cb(entry.docs)

        return () => {
            entry.listeners.delete(cb)
            if (entry.listeners.size === 0) {
                entry.unsub?.()
                cache.delete(`${collection}:${client}`)
            }
        }
    }, [collection])

    return docs
}

export default useSharedCollection
